import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IoSearchOutline } from 'react-icons/io5';
import './checkboxSetFilter.css';

// Free, Community-edition look-alike of AG Grid Enterprise's Set Filter:
// a checkbox list of distinct column values with search, Select All, and OK/Clear.
const CheckboxSetFilter = forwardRef((props, ref) => {
    const { api, colDef, column, valueGetter } = props;
    const field = colDef?.field;

    const getRawValue = useCallback((node) => {
        if (valueGetter) {
            return valueGetter({ node, data: node.data, colDef, column, api });
        }
        return node?.data ? node.data[field] : undefined;
    }, [valueGetter, colDef, field, column, api]);

    const formatValue = useCallback((raw, node) => {
        if (raw === null || raw === undefined || raw === '') return '(Blanks)';
        if (colDef?.valueFormatter) {
            const formatted = typeof colDef.valueFormatter === 'function'
                ? colDef.valueFormatter({ value: raw, data: node?.data, node, colDef, column, api })
                : raw;
            if (formatted !== null && formatted !== undefined && formatted !== '') return String(formatted);
        }
        return String(raw);
    }, [colDef, column, api]);

    const computeAllValues = useCallback(() => {
        const set = new Set();
        api.forEachNode((node) => {
            if (node.data) set.add(formatValue(getRawValue(node), node));
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }, [api, getRawValue, formatValue]);

    const allValuesRef = useRef(computeAllValues());
    const appliedRef = useRef(new Set(allValuesRef.current));
    const [uiValues, setUiValues] = useState(allValuesRef.current);
    const [selected, setSelected] = useState(() => new Set(allValuesRef.current));
    const [search, setSearch] = useState('');
    const searchInputRef = useRef(null);

    useImperativeHandle(ref, () => ({
        isFilterActive() {
            return appliedRef.current.size !== allValuesRef.current.length;
        },
        doesFilterPass(params) {
            const value = formatValue(getRawValue(params.node), params.node);
            return appliedRef.current.has(value);
        },
        getModel() {
            if (appliedRef.current.size === allValuesRef.current.length) return null;
            return { values: Array.from(appliedRef.current) };
        },
        setModel(model) {
            allValuesRef.current = computeAllValues();
            appliedRef.current = model?.values ? new Set(model.values) : new Set(allValuesRef.current);
        },
        afterGuiAttached() {
            allValuesRef.current = computeAllValues();
            setUiValues(allValuesRef.current);
            setSelected(new Set(appliedRef.current));
            setSearch('');
            requestAnimationFrame(() => searchInputRef.current?.focus());
        },
    }));

    const filteredValues = useMemo(() => {
        if (!search.trim()) return uiValues;
        const q = search.toLowerCase();
        return uiValues.filter((v) => v.toLowerCase().includes(q));
    }, [uiValues, search]);

    const allFilteredSelected = filteredValues.length > 0 && filteredValues.every((v) => selected.has(v));

    const toggleValue = (v) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(v)) next.delete(v); else next.add(v);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredValues.forEach((v) => next.delete(v));
            } else {
                filteredValues.forEach((v) => next.add(v));
            }
            return next;
        });
    };

    const closePopup = () => {
        props.api?.hidePopupMenu?.();
    };

    const applyAndClose = (newSelected) => {
        appliedRef.current = new Set(newSelected);
        props.filterChangedCallback?.();
        closePopup();
    };

    const handleOk = () => applyAndClose(selected);
    const handleClear = () => applyAndClose(new Set(uiValues));

    return (
        <div className="csf-panel">
            <div className="csf-search">
                <IoSearchOutline className="csf-search-icon" />
                <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <div className="csf-select-all">
                <label>
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} />
                    <span>Select All</span>
                </label>
            </div>
            <div className="csf-values">
                {filteredValues.length === 0 ? (
                    <div className="csf-empty">No values</div>
                ) : (
                    filteredValues.map((v) => (
                        <label key={v} className="csf-value-row">
                            <input type="checkbox" checked={selected.has(v)} onChange={() => toggleValue(v)} />
                            <span>{v}</span>
                        </label>
                    ))
                )}
            </div>
            <div className="csf-actions">
                <button type="button" className="csf-btn csf-btn-clear" onClick={handleClear}>Clear</button>
                <button type="button" className="csf-btn csf-btn-ok" onClick={handleOk}>OK</button>
            </div>
        </div>
    );
});

export default CheckboxSetFilter;
